import { useEffect, useRef } from "react";
import { useUserInfo, useUserToken } from "@/store/userStore";
import { initializeEcho, disconnectEcho, subscribeToPrivateChannel, unsubscribeFromPrivateChannel } from "@/services/echo";
import { addNotification, type Notification } from "@/store/notificationStore";
import { useSettingStore } from "@/store/settingStore";
import { playNotificationSound } from "@/utils/notification-sound";
import { toast } from "sonner";

interface NotificationData {
	message: string;
	title?: string;
	type?: "info" | "success" | "error";
	id?: string;
	created_at?: string; // ISO date string from backend
	[key: string]: unknown;
}

/**
 * Channel name pattern for private notifications
 * 
 * IMPORTANT: Laravel Echo's private() method automatically adds "private-" prefix
 * So if you return "user.1", the actual channel will be "private-user.1"
 * 
 * Change this to customize the channel name format
 * Default: "user.{userId}" -> results in "private-user.1", "private-user.2", etc.
 */
const getChannelName = (userId: string | number): string => {
	// You can change this pattern to match your backend
	// Note: Echo will add "private-" prefix automatically
	// Examples of what you return vs. final channel name:
	// return `user.${userId}`;                    // Returns: user.1 -> Final: private-user.1
	// return `notifications.${userId}`;          // Returns: notifications.1 -> Final: private-notifications.1
	// return `user-${userId}`;                   // Returns: user-1 -> Final: private-user-1
	// return `custom.${userId}.notifications`;   // Returns: custom.1.notifications -> Final: private-custom.1.notifications
	
	return `user.${userId}`;
};

/**
 * Hook to manage Laravel Echo connection and listen to notifications
 */
export const useEcho = () => {
	const userInfo = useUserInfo();
	const { accessToken } = useUserToken();
	const channelRef = useRef<ReturnType<typeof subscribeToPrivateChannel> | null>(null);
	const channelNameRef = useRef<string | null>(null);

	useEffect(() => {
	

		// Only initialize if user is logged in and has an ID
		if (!accessToken || !userInfo.id) {
	
			// Clean up if user logs out
			if (channelRef.current && channelNameRef.current) {
				unsubscribeFromPrivateChannel(channelNameRef.current);
				channelRef.current = null;
				channelNameRef.current = null;
			}
			disconnectEcho();
			return;
		}

		try {
		
			// Initialize Echo (this will clean up existing instance if any)
			initializeEcho(accessToken, userInfo.id);

			// Subscribe to private channel using the configured pattern
			const channelName = getChannelName(userInfo.id);
			
			// Unsubscribe from previous channel if user ID changed
			if (channelRef.current && channelNameRef.current && channelNameRef.current !== channelName) {
				unsubscribeFromPrivateChannel(channelNameRef.current);
				channelRef.current = null;
			}

			const channel = subscribeToPrivateChannel(channelName);

			if (channel) {
				channelRef.current = channel;
				channelNameRef.current = channelName;

				// Add subscription event listeners using Pusher's event system
				const pusherChannel = (channel as any).pusherChannel;
				if (pusherChannel) {
					pusherChannel.bind("pusher:subscription_succeeded", () => {
					});

					pusherChannel.bind("pusher:subscription_error", () => {
						toast.error(`Hiba a csatorna feliratkozásakor`);
					});
				}

				// Listen to the private-notification event
				channel.listen(".private-notification", (data: NotificationData) => {
					if (useSettingStore.getState().settings.notificationSoundEnabled !== false) {
						playNotificationSound();
					}

					// Add notification to localStorage
					if (data.message) {
						const notificationId = data.id || `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
						
						// Extract message, title, and type, then spread the rest
						const { message, title, type, created_at, ...rest } = data;
						
						const notification: Notification = {
							id: notificationId,
							message: message,
							title: title,
							type: (type || "info") as "info" | "success" | "error",
							// Use created_at from backend if provided, otherwise use current time
							createdAt: (created_at as string) || new Date().toISOString(),
							...rest,
						};

						
						try {
							addNotification(notification);	
						} catch (error) {
						}

						// Show toast with appropriate variant based on type
						const toastOptions = {
							position: "top-right" as const,
							duration: 5000,
							description: title ? message : undefined,
							closeButton: false,
							onClick: () => {
								// Toast will dismiss on click
							},
						};

						if (type === "error") {
							toast.error(title || message, toastOptions);
						} else if (type === "success") {
							toast.success(title || message, toastOptions);
						} else {
							toast.info(title || message, toastOptions);
						}
					}
				});

			} else {
			}
		} catch (error) {
			toast.error("Hiba a valós idejű értesítések inicializálásakor");
		}

		// Cleanup on unmount or when dependencies change
		return () => {
			if (channelRef.current && channelNameRef.current) {
				unsubscribeFromPrivateChannel(channelNameRef.current);
				channelRef.current = null;
				channelNameRef.current = null;
			}
		};
	}, [accessToken, userInfo.id]);

	return {
		isConnected: channelRef.current !== null,
	};
};

